<script lang="ts">
 /**
 * Drawer for selecting a container from the user's inventory.
 *
 * @remarks
 * Used by the capture flow to switch the active container context.
 * Presents two sections: recently used containers (for quick access)
 * and all containers (searchable). Uses {@link ContainerRow} with the
 * `picker` variant (no progress bar, no chevron, no link navigation).
 *
 * **Search behavior:** Filters the "All Containers" list by name and
 * location path. The "Recent" section hides when a search query is active.
 * Uses a fixed height (70% of the viewport via `--app-height`, since the
 * Android WebView reports 0 for `vh` units) to prevent layout jumping as the
 * filtered list changes length.
 *
 * **Reuses shared components:** {@link SearchInput} for the search field,
 * {@link ContainerRow} for each list item, {@link SbList} / {@link SbListLabel}
 * for list structure.
 */
</script>

<script setup lang="ts">
import { SbList, SbListLabel } from "@unio/saltbox-ui"
import { useVModel } from "@vueuse/core"
import { computed, ref } from "vue"

import ContainerRow from "@/components/ContainerRow.vue"
import SearchInput from "@/components/SearchInput.vue"
import { ICON } from "@/lib/icons"
import type { ContainerSummary } from "@/types/models"

const props = withDefaults(
  defineProps<{
    /** Whether the drawer is open. Two-way bound via update:open. */
    open: boolean
    /** Recently used containers, shown at the top for quick access. */
    recentContainers: ContainerSummary[]
    /** All containers in the account, filtered by the search query. */
    allContainers: ContainerSummary[]
    /** Whether to show the optional text search. Defaults to the existing behavior. */
    searchable?: boolean
    /** Whether to offer an explicit no-container action. */
    allowClear?: boolean
  }>(),
  { searchable: true, allowClear: false },
)

const emit = defineEmits<{
  /** Fired when the drawer opens or closes. */
  "update:open": [value: boolean]
  /** Fired when the user taps a container row. Passes the selected container. */
  select: [container: ContainerSummary]
  /** Fired when the user explicitly chooses no container. */
  clear: []
}>()

/** Two-way binding for the drawer open state via VueUse's useVModel. */
const drawerOpen = useVModel(props, "open", emit)

/** Current search query. Bound to the SearchInput v-model. */
const search = ref("")

/**
 * Containers filtered by the search query.
 *
 * @remarks
 * Matches against both name and location_path (with null-safe fallback
 * for top-level containers whose location_path is null).
 * Returns all containers when the search field is empty.
 */
const filteredContainers = computed(() => {
  if (!search.value) return props.allContainers
  const q = search.value.toLowerCase()
  return props.allContainers.filter(
    (c) => c.name.toLowerCase().includes(q) || (c.location_path ?? "").toLowerCase().includes(q),
  )
})

/** Whether to show the "Recent" section. Hidden when searching or when there are no recents. */
const showRecent = computed(() => !search.value && props.recentContainers.length > 0)
</script>

<template>
  <UDrawer v-model:open="drawerOpen">
    <template #content>
      <div class="flex h-[calc(var(--app-height,100vh)*0.7)] flex-col gap-4 p-4">
        <SearchInput v-if="searchable" v-model="search" placeholder="Search containers..." />

        <UButton
          v-if="allowClear"
          block
          color="neutral"
          variant="outline"
          :icon="ICON.noContainer"
          @click="$emit('clear')"
        >
          No container
        </UButton>

        <div class="flex-1 overflow-y-auto">
          <SbList v-if="showRecent" separator>
            <SbListLabel header>Recent</SbListLabel>
            <ContainerRow
              v-for="container in recentContainers"
              :key="container.id"
              :container="container"
              variant="picker"
              @click="$emit('select', container)"
            />
          </SbList>

          <SbList separator>
            <SbListLabel header>All Containers</SbListLabel>
            <ContainerRow
              v-for="container in filteredContainers"
              :key="container.id"
              :container="container"
              variant="picker"
              @click="$emit('select', container)"
            />
          </SbList>
        </div>

        <button class="text-primary flex items-center gap-2 text-sm font-medium">
          <UIcon :name="ICON.add" />
          <span>Create new container</span>
        </button>
      </div>
    </template>
  </UDrawer>
</template>
